import React, { useState } from "react";
import { Spinner } from "react-bootstrap";
import AppSelect from "../components/ui/AppSelect";

/**
 * Power BI–inspired design system for the Ticketing screens, mirroring the
 * tokens used across the dashboard (DeliveryFeeSettings / VendorWallet). Navy
 * hero band + light page + white panels with a coloured header dot + KPI tiles.
 * Ticketing's accent is Lusuku green; the shared palette (blue/teal/orange/
 * purple) is available for KPI variety.
 */
export const C = {
  bg: "#f4f6fb",
  panel: "#ffffff",
  header: "#1a2b4a",
  green: "#2e7d32",
  greenSoft: "#e8f5e9",
  greenGrad: "linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)",
  accent: "#e63757",
  blue: "#2c7be5",
  blueSoft: "#eaf2ff",
  teal: "#00b69b",
  tealSoft: "#e6faf7",
  orange: "#f6961e",
  orangeSoft: "#fff4e6",
  purple: "#7048e8",
  purpleSoft: "#f1edff",
  text: "#1a2b4a",
  textMid: "#4a5568",
  textLight: "#8898aa",
  border: "#e2e8f0",
  inputBg: "#f8faff",
  shadow: "0 2px 12px rgba(26,43,74,0.08)",
  radius: "10px",
  radiusSm: "6px",
};

export const S = {
  page: { background: C.bg, minHeight: "100%", padding: "0 0 48px 0" },
  hero: {
    background: `linear-gradient(135deg, ${C.header} 0%, #243b6e 100%)`,
    padding: "26px 30px 24px", marginBottom: 24, borderRadius: C.radius, color: "#fff",
    display: "flex", alignItems: "flex-start", justifyContent: "space-between",
    flexWrap: "wrap", gap: 16, boxShadow: "0 4px 24px rgba(26,43,74,0.18)",
  },
  heroTitle: { fontSize: 22, fontWeight: 700, letterSpacing: "-0.3px", marginBottom: 4, color: "#fff" },
  heroSub: { fontSize: 13, color: "rgba(255,255,255,0.72)", marginBottom: 0, maxWidth: 640 },
  chip: (on) => ({
    background: on ? "rgba(0,182,155,0.22)" : "rgba(255,255,255,0.12)",
    border: `1px solid ${on ? "rgba(0,182,155,0.5)" : "rgba(255,255,255,0.25)"}`,
    borderRadius: 20, padding: "5px 14px", fontSize: 12, fontWeight: 700,
    color: on ? "#7ff0dd" : "rgba(255,255,255,0.85)", display: "inline-flex", alignItems: "center", gap: 6,
  }),
  panel: { background: C.panel, borderRadius: C.radius, boxShadow: C.shadow, marginBottom: 20, overflow: "hidden", border: `1px solid ${C.border}` },
  panelHead: (color) => ({ background: `linear-gradient(90deg, ${color}14 0%, transparent 100%)`, borderBottom: `1px solid ${C.border}`, padding: "13px 20px", display: "flex", alignItems: "center", gap: 10 }),
  dot: (color) => ({ width: 10, height: 10, borderRadius: "50%", background: color, flexShrink: 0 }),
  panelTitle: { fontSize: 13, fontWeight: 700, color: C.text, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 0 },
  panelSub: { fontSize: 11, color: C.textLight, marginLeft: "auto", textAlign: "right" },
  panelBody: { padding: "20px" },
  label: { fontSize: 11, fontWeight: 700, color: C.textMid, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4, display: "block" },
  hint: { fontSize: 11, color: C.textLight, marginTop: 3 },
  input: { background: C.inputBg, border: `1px solid ${C.border}`, borderRadius: C.radiusSm, fontSize: 13, color: C.text, padding: "8px 11px", width: "100%", outline: "none", transition: "border-color 0.15s", height: 38 },
  kpi: (color) => ({ background: C.panel, borderRadius: C.radius, padding: "16px 18px", boxShadow: C.shadow, borderLeft: `4px solid ${color}`, height: "100%" }),
  kpiVal: { fontSize: 22, fontWeight: 700, color: C.text, lineHeight: 1.1 },
  kpiLabel: { fontSize: 11, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.6px", marginTop: 3 },
  sectionBreak: { fontSize: 10, fontWeight: 800, letterSpacing: "1px", textTransform: "uppercase", color: C.textLight, margin: "16px 0 10px", display: "flex", alignItems: "center", gap: 8 },
  th: { fontSize: 10, fontWeight: 800, letterSpacing: "0.6px", textTransform: "uppercase", color: C.textLight, borderBottom: `1px solid ${C.border}`, padding: "8px 10px", textAlign: "left" },
  td: { fontSize: 13, color: C.text, borderBottom: `1px solid ${C.border}`, padding: "9px 10px", verticalAlign: "middle" },
  badge: (color, soft) => ({ background: soft, color, fontSize: 10.5, fontWeight: 800, letterSpacing: "0.4px", padding: "3px 9px", borderRadius: 20, textTransform: "uppercase" }),
};

export const Page = ({ children }) => <div style={S.page}>{children}</div>;

export const Hero = ({ title, subtitle, right }) => (
  <div style={S.hero}>
    <div><div style={S.heroTitle}>{title}</div><div style={S.heroSub}>{subtitle}</div></div>
    {right && <div>{right}</div>}
  </div>
);

export const Panel = ({ title, sub, color = C.green, children, style }) => (
  <div style={{ ...S.panel, ...style }}>
    {title && (
      <div style={S.panelHead(color)}>
        <span style={S.dot(color)} />
        <span style={S.panelTitle}>{title}</span>
        {sub && <span style={S.panelSub}>{sub}</span>}
      </div>
    )}
    <div style={S.panelBody}>{children}</div>
  </div>
);

export const Field = ({ label, hint, children, style }) => (
  <div style={{ marginBottom: 14, ...style }}>
    {label && <label style={S.label}>{label}</label>}
    {children}
    {hint && <div style={S.hint}>{hint}</div>}
  </div>
);

export const Kpi = ({ label, value, color = C.green }) => (
  <div style={S.kpi(color)}>
    <div style={S.kpiVal}>{value}</div>
    <div style={S.kpiLabel}>{label}</div>
  </div>
);

export const PInput = ({ style, ...rest }) => {
  const [f, setF] = useState(false);
  return (
    <input {...rest} style={{ ...S.input, borderColor: f ? C.green : C.border, ...style }}
      onFocus={(e) => { setF(true); rest.onFocus && rest.onFocus(e); }}
      onBlur={(e) => { setF(false); rest.onBlur && rest.onBlur(e); }} />
  );
};

export const PSelect = ({ style, children, ...rest }) => {
  const [f, setF] = useState(false);
  return (
    <AppSelect {...rest} style={{ ...S.input, borderColor: f ? C.green : C.border, cursor: "pointer", ...style }}
      onFocus={(e) => { setF(true); rest.onFocus && rest.onFocus(e); }}
      onBlur={(e) => { setF(false); rest.onBlur && rest.onBlur(e); }}>
      {children}
    </AppSelect>
  );
};

/** Power BI button — label always visible (never hidden on submit), inline spinner. */
export const PButton = ({ children, loading, variant = "solid", disabled, type = "button", style, onMouseOver, onMouseOut, ...rest }) => {
  const isDisabled = loading || disabled;
  const solid = variant === "solid";
  const danger = variant === "danger";
  const base = {
    solid: { background: isDisabled ? "#b7c4b7" : C.greenGrad, color: "#fff", border: "none", boxShadow: isDisabled ? "none" : "0 2px 8px rgba(46,125,50,0.32)" },
    outline: { background: "transparent", color: C.green, border: `1px solid ${C.green}` },
    ghost: { background: "transparent", color: C.textMid, border: `1px solid ${C.border}` },
    danger: { background: isDisabled ? "#e0a0a0" : "linear-gradient(135deg,#e63757 0%,#c0203e 100%)", color: "#fff", border: "none" },
  }[variant] || {};
  return (
    <button type={type} disabled={isDisabled} {...rest}
      style={{ borderRadius: C.radiusSm, fontWeight: 600, fontSize: 13, padding: "9px 20px",
        cursor: isDisabled ? "not-allowed" : "pointer", opacity: isDisabled ? 0.85 : 1,
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
        transition: "all .15s", whiteSpace: "nowrap", ...base, ...style }}>
      {loading && <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />}
      <span style={{ color: "inherit" }}>{children}</span>
    </button>
  );
};
